import { z } from 'zod';
import { COMPLETENESS_FLAGS } from '../enums.js';
import { isoDate, uuid } from './common.js';
import { teamMemberSchema } from './org.js';
/**
 * `GET /dashboard` (FR-DASH-1..3, ADR-044): one response whose sections are present only when the
 * caller holds their permission — absent sections are `null`, never empty placeholders. Salary
 * totals (`payroll`) only with `salary.read.all` (AC-3).
 */
/** Profile items an employee can complete themselves (FR-DASH-1, ADR-044), in display order. */
export const PROFILE_CHECKLIST_ITEMS = [
    'DATE_OF_BIRTH',
    'GENDER',
    'BLOOD_GROUP',
    'PERSONAL_EMAIL',
    'PRESENT_ADDRESS',
    'PERMANENT_ADDRESS',
    'EMERGENCY_CONTACT',
    'NID_NUMBER',
    'BANK_ACCOUNT',
    'NID_DOCUMENT',
];
export const PROFILE_CHECKLIST_LABELS = {
    DATE_OF_BIRTH: 'Date of birth',
    GENDER: 'Gender',
    BLOOD_GROUP: 'Blood group',
    PERSONAL_EMAIL: 'Personal email',
    PRESENT_ADDRESS: 'Present address',
    PERMANENT_ADDRESS: 'Permanent address',
    EMERGENCY_CONTACT: 'Emergency contact',
    NID_NUMBER: 'NID / Birth ID number',
    BANK_ACCOUNT: 'Bank account',
    NID_DOCUMENT: 'NID document',
};
const personRef = z.object({ id: uuid, empCode: z.number().int(), fullName: z.string() });
export const dashboardSchema = z.object({
    /** FR-DASH-1 — the caller's own profile (`self.read` with an employee record). */
    me: z
        .object({
        checklist: z.array(z.object({ item: z.enum(PROFILE_CHECKLIST_ITEMS), done: z.boolean() })),
        /** Whole percent of checklist items done. */
        percent: z.number().int().min(0).max(100),
        /** Own approval requests still pending (e.g. a Bank & TIN change). */
        pendingRequests: z.number().int().min(0),
    })
        .nullable(),
    /** FR-DASH-2 — `team.read`: the reporting tree size and the direct reports. */
    team: z
        .object({
        size: z.number().int().min(0),
        directReports: z.array(teamMemberSchema),
    })
        .nullable(),
    /** FR-DASH-3 — `employee.read.all`: today's status counts; departments count current staff. */
    headcount: z
        .object({
        active: z.number().int(),
        upcoming: z.number().int(),
        noticePeriod: z.number().int(),
        exited: z.number().int(),
        byDepartment: z.array(z.object({
            departmentId: uuid.nullable(),
            name: z.string().nullable(),
            count: z.number().int(),
        })),
    })
        .nullable(),
    /** FR-DASH-3 — `employee.read.all`: joining dates and last working days this month (Dhaka). */
    movements: z
        .object({
        month: z.string().regex(/^\d{4}-\d{2}$/),
        joiners: z.array(personRef.extend({ date: isoDate })),
        leavers: z.array(personRef.extend({ date: isoDate })),
    })
        .nullable(),
    /** FR-DASH-3 — `bank.approve`: requests the caller may decide now. */
    approvals: z.object({ pending: z.number().int().min(0) }).nullable(),
    /** FR-DASH-3 — `employee.update.all`: people (not exited) per completeness flag the caller may see (ADR-037). */
    dataGaps: z
        .array(z.object({ flag: z.enum(COMPLETENESS_FLAGS), count: z.number().int().min(0) }))
        .nullable(),
    /**
     * FR-DASH-3 — `salary.read.all` only (AC-3): today's salaries of active and notice-period
     * staff — total gross, the bank (A/C) part and the cash part.
     */
    payroll: z
        .object({
        employees: z.number().int(),
        gross: z.number().int(),
        acAmount: z.number().int(),
        cashAmount: z.number().int(),
    })
        .nullable(),
});
//# sourceMappingURL=dashboard.js.map